import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { Box, Modal, Backdrop, CircularProgress, Typography, IconButton, Checkbox, Chip, TextField, InputAdornment } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AnimationPage from '../../../pages/AnimationPage';
import { formatterCurrency, exportStyledExcel } from '../../../utils/methods';
import CommonSelect from '../../../form-components/CommonSelect';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import AdjustAmountModal from './AdjustAmountModal';
import EditLessonAllowanceModal from './EditLessonAllowanceModal';
import {
  getSettlementDetail,
  getSettlementLessons,
  addSettlementAdjustment,
  updateSettlementLessonAllowance,
  confirmSettlement,
  deleteSettlement,
  recalculateSettlement,
  getRecentSettlements,
  updateWorkHours,
} from '../../../api/salarySettlement';
import { getSalaryPeriod } from '../../../api/salarySetting';

import {
  PageContainer, ProfilePanel, ProfileCard, ProfileAvatar, ProfileName, ProfilePosition,
  SettlementBadge, ProfileInfoTable, ProfileInfoRow, ProfileInfoLabel, ProfileInfoValue,
  ProfileInfoHighlight, RegistrationCard, RegistrationTitle, RegistrationRow,
  RegistrationLabel, RegistrationValue, ContentPanel, StepTabsContainer, StepTab,
  StepNumber, StepConnector, BackToListButton, SummaryCardsContainer, SummaryCard,
  SummaryIconBox, SummaryLabel, SummaryValue, LessonTabsContainer, LessonTab,
  TableHeader, TableHeaderLeft, TableHeaderTitle, PeriodText, TotalAmountBox,
  TotalAmountLabel, TotalAmountValue, StyledTable, StyledTableHead, StyledTableHeadCell,
  StyledTableBody, StyledTableRow, StyledTableCell, FooterBar, CancelButton, NextButton,
  PrevButton, PaySummaryCard, PaySummaryIcon, PaySummaryLabel, PaySummaryValue,
  PayItemsContainer, PayItemsColumn, PayItemsHeader, PayItemsHeaderTitle,
  PayItemsHeaderAmount, PayItemCard, PayItemRow, PayItemTitle, PayItemAmount,
  AdjustButton, PayItemSubRow, PayItemSubLabel, PayItemSubAmount, PayItemsWrapper,
  PayslipContainer, PayslipCard, PayslipHeader, PayslipHeaderLeft, PayslipBranchName,
  PayslipTitle, PayslipEmployeeInfo, PayslipEmployeeInfoText, PayslipEmployeeInfoDivider,
  PayslipHeaderRight, PayslipRecipientLabel, PayslipRecipientName,
  PayslipBody, PayslipSection, PayslipSectionHeader, PayslipSectionTitleBox,
  PayslipSectionDot, PayslipSectionTitle, PayslipSectionSubtitle, PayslipSectionAmount,
  PayslipDivider, PayslipItemsGrid, PayslipItemBox, PayslipItemBoxHeader,
  PayslipItemBoxTitle, PayslipItemBoxAmount, PayslipItemBoxSubRow,
  PayslipItemBoxSubLabel, PayslipItemBoxSubAmount,
  PayslipTotalBox, PayslipTotalLabel, PayslipTotalTitle, PayslipTotalSub,
  PayslipTotalAmount, PayslipFooterText, RecentPayCard, RecentPayHeader, RecentPayTitle,
  RecentPayPeriod, RecentPayTableHead, RecentPayTableHeadCell, RecentPayRow,
  RecentPayMonth, RecentPayAmount, RecentPayTableWrapper, ConfirmModalContainer, ConfirmModalText,
  ConfirmModalButtons,
  EarlyNoticeBox,
  EarlyNoticeTitle,
  EarlyNoticeRow,
  EarlyNoticeDesc,
} from './SalarySettlementStyled';

const STEPS = [
  { id: 1, label: '수업 이력' },
  { id: 2, label: '급여 항목' },
  { id: 3, label: '명세서' },
];

const SALARY_TYPE_LABELS = { COUNT: '횟수제', MONTHLY: '월급제', HOURLY: '시간제' };
const STATUS_LABELS = { DRAFT: '정산중', CONFIRMED: '정산완료' };
const CLASSIFICATION_LABELS = { ACTIVE: '재직중', LEFT: '퇴사', LEAVE: '휴직' };
const TARGET_ITEM_MAP = {
  BASE: '기본급', PRIVATE_LESSON: '개인레슨 수당', GROUP_LESSON: '그룹레슨 수당',
  OVERFLOW: '초과 매출/수업 수당', BONUS: '성과급', TAX_EXEMPT: '비과세 항목',
  OVERTIME: '연장근로 수당', NIGHT_WORK: '야간근로 수당',
  HOLIDAY_WORK: '휴일근로 수당', WEEKLY_HOLIDAY: '주휴 수당',
  DEDUCTION: '공제',
};

const SalarySettlementDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { id: settlementId } = useParams();
  const user = useSelector(state => state.user.user);
  const backUrl = `/salarySettlement?year=${searchParams.get('year') || ''}&month=${searchParams.get('month') || ''}`;

  const [currentStep, setCurrentStep] = useState(() => {
    const step = searchParams.get('step');
    return step ? Number(step) : 1;
  });
  const [lessonTab, setLessonTab] = useState('private');
  const [memberKeyword, setMemberKeyword] = useState('');
  const [rangeFilter, setRangeFilter] = useState('');
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [workHours, setWorkHours] = useState({ totalWorkHours: 0, overtimeHours: 0, nightWorkHours: 0, holidayWorkHours: 0, weeklyHolidayHours: 0 });
  // 근로시간 입력값을 이미 초기화한 정산 id (정산이 바뀌기 전까지는 사용자 입력을 덮어쓰지 않기 위함)
  const seededWorkHoursSettlementId = useRef(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [editLessonModalOpen, setEditLessonModalOpen] = useState(false);
  const [editLessonTarget, setEditLessonTarget] = useState(null);
  const currentBranchId = user.branch?.branchId;
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);
  // 재계산 시 유지할(=수정값을 그대로 보존할) 수업이력 id 모음. 재계산 확인 모달을 열 때
  // 수정된 건 전체로 초기화하고, 체크 해제한 건만 재계산 결과로 초기화된다.
  const [keepLessonIds, setKeepLessonIds] = useState(new Set());

  // ============================================
  // API 조회
  // ============================================
  const { data: settlement } = useQuery({
    queryKey: ['settlementDetail', settlementId],
    queryFn: () => getSettlementDetail(settlementId, currentBranchId, user.token),
    enabled: !!settlementId && !!currentBranchId && !!user.token,
  });

  // 이 지점의 "수업 사인 완료된 것만 정산" 현재 설정값. 화면에서 바로 보여주기 위한 조회일 뿐,
  // 정산 계산 자체는 백엔드가 이 값을 그대로 참조한다(프론트가 재계산하지 않음).
  const { data: periodSetting } = useQuery({
    queryKey: ['salaryPeriod', settlement?.branchId],
    queryFn: () => getSalaryPeriod(settlement.branchId, user.token),
    enabled: !!settlement?.branchId,
  });
  const signedOnly = periodSetting?.signedOnlyYn === 'Y';

  const { data: privateLessons = [] } = useQuery({
    queryKey: ['settlementLessons', settlementId, 'PRIVATE'],
    queryFn: () => getSettlementLessons(settlementId, currentBranchId, 'PRIVATE', user.token),
    // 1단계 화면뿐 아니라 확정·재계산 확인창에서도 "아직 진행 전인 수업" 건수를 세야 해서
    // 단계와 무관하게 불러온다.
    enabled: !!settlementId && !!currentBranchId && !!user.token,
    select: data => data || [],
  });

  const { data: groupLessons = [] } = useQuery({
    queryKey: ['settlementLessons', settlementId, 'GROUP'],
    queryFn: () => getSettlementLessons(settlementId, currentBranchId, 'GROUP', user.token),
    // 1단계 화면뿐 아니라 확정·재계산 확인창에서도 "아직 진행 전인 수업" 건수를 세야 해서
    // 단계와 무관하게 불러온다.
    enabled: !!settlementId && !!currentBranchId && !!user.token,
    select: data => data || [],
  });

  // 산정 기간이 끝나기 전에 정산하면, 아직 진행되지 않은 수업이 출결 없이(인원 0명)
  // 스냅샷된다. 확정하면 재계산이 막혀 나중에 출결이 기록돼도 반영되지 않으므로,
  // 계산·확정 확인창에서 무엇이 반영됐고 무엇이 아직인지 먼저 알려준다.
  const earlySettlementNotice = (() => {
    const periodEnd = settlement?.periodEndDate;
    if (!periodEnd) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(`${periodEnd}T00:00:00`);
    if (Number.isNaN(endDate.getTime()) || endDate <= today) return null;

    const toDateString = d =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayText = toDateString(today);
    const allLessons = [...privateLessons, ...groupLessons];
    const pendingCount = allLessons.filter(l => l.lessonDate && l.lessonDate > todayText).length;

    return {
      periodStart: settlement.periodStartDate,
      periodEnd,
      today: todayText,
      daysLeft: Math.ceil((endDate - today) / 86400000),
      totalCount: allLessons.length,
      pendingCount,
      settledCount: allLessons.length - pendingCount,
    };
  })();

  const { data: recentPay = [] } = useQuery({
    queryKey: ['recentSettlements', settlement?.employeeId, settlement?.branchId],
    queryFn: () => getRecentSettlements(settlement.employeeId, settlement.branchId, 6, user.token),
    enabled: !!settlement?.employeeId && currentStep === 3,
    select: data => data || [],
  });

  // ============================================
  // Mutations
  // ============================================
  const adjustMutation = useMutation({
    mutationFn: data => addSettlementAdjustment(settlementId, settlement?.branchId, data, user.token),
    onSuccess: () => {
      toast.success('금액이 조정되었습니다');
      queryClient.invalidateQueries({ queryKey: ['settlementDetail', settlementId] });
    },
  });

  const editLessonMutation = useMutation({
    mutationFn: data =>
      updateSettlementLessonAllowance(settlementId, editLessonTarget?.lessonId, settlement?.branchId, data, user.token),
    onSuccess: () => {
      toast.success('수당이 수정되었습니다');
      queryClient.invalidateQueries({ queryKey: ['settlementDetail', settlementId] });
      queryClient.invalidateQueries({ queryKey: ['settlementLessons', settlementId, editLessonTarget?.lessonType] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmSettlement(settlementId, settlement?.branchId, user.token),
    onSuccess: () => {
      toast.success('정산이 확정되었습니다');
      setConfirmModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['settlementList'] });
      queryClient.invalidateQueries({ queryKey: ['settlementDetail'] });
      navigate(backUrl);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSettlement(settlementId, settlement?.branchId, user.token),
    onSuccess: () => {
      toast.success('정산이 삭제되었습니다');
      navigate(backUrl);
    },
  });

  const recalculateMutation = useMutation({
    mutationFn: keepIds => recalculateSettlement(settlementId, settlement?.branchId, keepIds, user.token),
    onSuccess: newSettlement => {
      toast.success('재계산이 완료되었습니다');
      navigate(`/salarySettlement/${newSettlement.id}?year=${searchParams.get('year') || ''}&month=${searchParams.get('month') || ''}`, { replace: true });
    },
  });

  const workHoursMutation = useMutation({
    mutationFn: () => updateWorkHours(settlementId, settlement?.branchId, workHours, user.token),
    onSuccess: () => {
      toast.success('근로수당이 적용되었습니다');
      queryClient.invalidateQueries({ queryKey: ['settlementDetail', settlementId] });
    },
  });

  const handleOpenAdjust = (itemName, itemType, currentAmount, targetKey) => {
    setAdjustTarget({ itemName, itemType, currentAmount, targetKey });
    setAdjustModalOpen(true);
  };

  // 수기로 수정한 적 있는 수업이력. 재계산 확인 모달에서 유지할 항목을 고르는 목록으로 쓴다.
  const editedLessons = useMemo(
    () => [...privateLessons, ...groupLessons].filter(l => l.edited),
    [privateLessons, groupLessons],
  );

  const handleOpenRecalculate = () => {
    // 기본값은 "전체 유지" — 체크 해제한 항목만 재계산 값으로 초기화된다.
    setKeepLessonIds(new Set(editedLessons.map(l => l.id)));
    setRecalculateModalOpen(true);
  };

  const toggleKeepLesson = lessonId => {
    setKeepLessonIds(prev => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  const handleOpenEditLesson = (lesson, lessonType, lessonLabel) => {
    setEditLessonTarget({
      lessonId: lesson.id,
      lessonType,
      lessonLabel,
      currentAmount: lesson.lessonAllowance,
      editMemo: lesson.editMemo,
    });
    setEditLessonModalOpen(true);
  };

  const handleCancel = () => navigate(backUrl);
  const handleNext = () => currentStep < 3 && setCurrentStep(prev => prev + 1);
  const handlePrev = () => currentStep > 1 && setCurrentStep(prev => prev - 1);

  const isConfirmed = settlement?.status === 'CONFIRMED';
  const formatTime = t => t ? t.substring(0, 5) : '';

  // 페이지 진입 시 스크롤 맨 위로
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 정산 데이터가 로드되면 저장된 근로시간 스냅샷으로 입력값을 초기화한다.
  // 같은 정산에서는 한 번만 초기화하여, 사용자가 입력 중인 값이나 방금 적용한 값을 덮어쓰지 않는다.
  useEffect(() => {
    if (!settlement || seededWorkHoursSettlementId.current === settlement.id) return;
    seededWorkHoursSettlementId.current = settlement.id;
    setWorkHours({
      totalWorkHours: (settlement.totalWorkMinutes || 0) / 60,
      overtimeHours: (settlement.overtimeMinutes || 0) / 60,
      nightWorkHours: (settlement.nightWorkMinutes || 0) / 60,
      holidayWorkHours: (settlement.holidayWorkMinutes || 0) / 60,
      weeklyHolidayHours: Number(settlement.weeklyHolidayHours || 0),
    });
  }, [settlement]);

  const s = settlement || {};
  // 수업 수당은 횟수제 전용이다(SalarySettlementService § isCountBasedSalary).
  // 월급제/시간제는 실제 수업 이력이 있어도 수당이 전부 0원으로 뜨므로,
  // 수업이력 테이블 본문을 안내 문구로 대체해 혼란을 막는다.
  const isCountType = s.salaryType === 'COUNT';

  // 수업이력 필터(개인레슨=회원명 검색, 그룹레슨=정산 구간). 필터 중 합계는 보이는 행의 수당 합.
  const keyword = memberKeyword.trim();
  const filteredPrivateLessons = keyword
    ? privateLessons.filter(l => (l.memberName || '').includes(keyword))
    : privateLessons;
  const privateTotalAmount = keyword
    ? filteredPrivateLessons.reduce((sum, l) => sum + (l.lessonAllowance || 0), 0)
    : s.privateLessonAmount || 0;
  // 드롭다운 옵션 = 이번 정산 수업들에 적용된 정산 구간(좌측 "구간 수당별 현황"과 같은 기준)
  const rangeOptions = [...new Set(groupLessons.map(l => l.rangeLabel).filter(Boolean))]
    .map(label => ({ value: label, label: `정산 구간 : ${label}` }));
  const filteredGroupLessons = rangeFilter
    ? groupLessons.filter(l => l.rangeLabel === rangeFilter)
    : groupLessons;
  const groupTotalAmount = rangeFilter
    ? filteredGroupLessons.reduce((sum, l) => sum + (l.lessonAllowance || 0), 0)
    : s.groupLessonAmount || 0;
  // 수업이력 엑셀. 화면 표와 같은 컬럼으로, 현재 필터가 적용된(보이는) 행만 내보낸다.
  // 금액·인원은 숫자로 넣고 서식만 입혀 엑셀에서 바로 합계·정렬이 되게 한다.
  const WON = '#,##0"원"';
  const PEOPLE = '#,##0"명"';
  const excelOptions = (type, filterLabel, totalAmount, count) => ({
    filename: `${s.settlementYear}년${s.settlementMonth}월_${s.employeeName || ''}_${type}_수업이력`,
    sheetName: `${type} 수업 이력`,
    variant: 'form',
    infoRows: [
      [
        { label: '지점', value: s.branchName || '-' },
        { label: '직원명', value: s.employeeName || '-' },
      ],
      [
        { label: '정산 기간', value: `${s.periodStartDate} ~ ${s.periodEndDate}` },
        { label: '조회 조건', value: filterLabel },
      ],
      [
        { label: '수업 건수', value: `${count}건` },
        { label: '수당 합계', value: `${formatterCurrency(totalAmount)}원` },
      ],
    ],
    // 결석 행만 빨간 글씨로 구분한다(개인레슨). 배경은 칠하지 않는다.
    rowFontColor: row => (row.출결 === '결석' ? 'C62828' : null),
  });
  const signedLabel = l => (l.signed ? '완료' : '미완료');
  // 화면의 "수정됨" 칩 + 수정 사유를 한 칸에 담는다.
  const editNote = l => (l.edited ? `수정됨${l.editMemo ? ` - ${l.editMemo}` : ''}` : '');

  const handlePrivateExcel = () =>
    exportStyledExcel(
      filteredPrivateLessons.map(l => ({
        수업일: l.lessonDate,
        '수업 시간': `${formatTime(l.startTime)}~${formatTime(l.endTime)}`,
        회원명: l.memberName,
        출결: l.attendanceStatus === 'ATTENDED' ? '출석' : '결석',
        '사인 여부': signedLabel(l),
        '신규 여부': l.isNewMember === 'Y' ? '신규' : '재등록',
        수강명: l.ticketName,
        판매금액: l.saleAmount ?? 0,
        결제금액: l.paidAmount ?? 0,
        '회당 단가': l.unitPrice ?? 0,
        '적용 구분': l.rateLabel,
        '정산된 수업 수당': l.lessonAllowance ?? 0,
        비고: editNote(l),
      })),
      {
        ...excelOptions(
          '개인레슨',
          keyword ? `회원 검색 : ${keyword}` : '전체',
          privateTotalAmount,
          filteredPrivateLessons.length,
        ),
        numFmts: { 판매금액: WON, 결제금액: WON, '회당 단가': WON, '정산된 수업 수당': WON },
        columnAlignments: { 수강명: 'left', 판매금액: 'right', 결제금액: 'right', '회당 단가': 'right', '정산된 수업 수당': 'right', 비고: 'left' },
        footerRow: { 수업일: '합계', '정산된 수업 수당': privateTotalAmount },
      },
    );

  const handleGroupExcel = () =>
    exportStyledExcel(
      filteredGroupLessons.map(l => ({
        수업일: l.lessonDate,
        '수업 시간': `${formatTime(l.startTime)}~${formatTime(l.endTime)}`,
        수업명: l.className,
        강의실: l.roomName,
        정원: l.capacity ?? 0,
        '수업 인원': l.enrolledCount ?? 0,
        '출석 인원': l.attendedCount ?? 0,
        '결석 인원': l.absentCount ?? 0,
        '사인 여부': signedLabel(l),
        '정산 구간': l.rangeLabel,
        '정산된 수업 수당': l.lessonAllowance ?? 0,
        비고: editNote(l),
      })),
      {
        ...excelOptions(
          '그룹레슨',
          rangeFilter ? `정산 구간 : ${rangeFilter}` : '전체',
          groupTotalAmount,
          filteredGroupLessons.length,
        ),
        numFmts: { 정원: PEOPLE, '수업 인원': PEOPLE, '출석 인원': PEOPLE, '결석 인원': PEOPLE, '정산된 수업 수당': WON },
        columnAlignments: { 수업명: 'left', '정산된 수업 수당': 'right', 비고: 'left' },
        footerRow: { 수업일: '합계', '정산된 수업 수당': groupTotalAmount },
      },
    );

  // 최저시급 보정이 반영된, 정산에 실제 적용되는 시급. 백엔드가 그대로 내려준다.
  // baseAmount / totalWorkMinutes 로 역산하면 안 된다 — baseAmount는 연장·휴일을 뺀
  // 소정근로시간 기준인데 totalWorkMinutes는 총시간이라 연장이 있으면 시급이 낮게 나온다.
  const settledHourlyRate = s.hourlyRate || 0;

  // 시간제 기본급은 소정근로시간(총 근무 − 연장 − 휴일)만 덮는다.
  // 연장·휴일 시간은 각 수당이 근로분까지 지급하므로 기본급에서 빠진다.
  // 수당이 꺼져 있으면 그 시간은 빠지지 않는다 — 백엔드 updateWorkHours 와 동일한 조건.
  const premiumHours =
    (s.overtimeEnabled === 'Y' ? workHours.overtimeHours : 0) +
    (s.holidayWorkEnabled === 'Y' ? workHours.holidayWorkHours : 0);
  const scheduledHours = Math.max(0, workHours.totalWorkHours - premiumHours);
  // 총 근무시간이 연장·휴일 합보다 적으면 백엔드가 거부한다. 버튼을 미리 막는다.
  const isWorkHoursInvalid =
    s.salaryType === 'HOURLY' && premiumHours > 0 && workHours.totalWorkHours < premiumHours;

  // ============================================
  // Step 1: 수업 이력
  // ============================================
  const renderStep1 = () => (
    <>
      <SummaryCardsContainer>
        <SummaryCard>
          <SummaryIconBox bgColor="#E6F7F8"><SchoolOutlinedIcon sx={{ color: '#4AB3BC', fontSize: '22px' }} /></SummaryIconBox>
          <Box><SummaryLabel>정산 수업</SummaryLabel><SummaryValue>{s.totalLessonCount || 0}건</SummaryValue></Box>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconBox bgColor="#FFF7E6"><AttachMoneyIcon sx={{ color: '#FA8C16', fontSize: '22px' }} /></SummaryIconBox>
          <Box><SummaryLabel>수당 합계</SummaryLabel><SummaryValue>{formatterCurrency((s.privateLessonAmount || 0) + (s.groupLessonAmount || 0))}원</SummaryValue></Box>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconBox bgColor="#E6F7F8"><PersonOutlineIcon sx={{ color: '#4AB3BC', fontSize: '22px' }} /></SummaryIconBox>
          <Box><SummaryLabel>개인레슨</SummaryLabel><SummaryValue>{s.privateLessonCount || 0}건</SummaryValue></Box>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconBox bgColor="#F0F5FF"><GroupsOutlinedIcon sx={{ color: '#597EF7', fontSize: '22px' }} /></SummaryIconBox>
          <Box><SummaryLabel>그룹레슨</SummaryLabel><SummaryValue>{s.groupLessonCount || 0}건</SummaryValue></Box>
        </SummaryCard>
      </SummaryCardsContainer>

      {settlement?.branchId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '12px' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: signedOnly ? '#FA8C16' : '#8c8c8c' }}>
            이 지점은 현재 "수업 사인 완료된 것만 정산"이 {signedOnly ? '켜져' : '꺼져'} 있습니다.
          </Typography>
          {settlement?.centerId && (
            <Typography
              component="span"
              onClick={() => navigate(`/store/${settlement.centerId}?tab=4&subtab=4`)}
              sx={{ fontSize: '13px', fontWeight: 600, color: '#4AB3BC', textDecoration: 'underline', cursor: 'pointer' }}
            >
              설정 바로가기
            </Typography>
          )}
        </Box>
      )}

      <LessonTabsContainer>
        <LessonTab active={lessonTab === 'private'} onClick={() => setLessonTab('private')}>개인레슨</LessonTab>
        <LessonTab active={lessonTab === 'group'} onClick={() => setLessonTab('group')}>그룹레슨</LessonTab>
      </LessonTabsContainer>

      {lessonTab === 'private' ? renderPrivateTable() : renderGroupTable()}
    </>
  );

  const renderPrivateTable = () => (
    <>
      <TableHeader>
        <TableHeaderLeft>
          <TableHeaderTitle>개인레슨 수업 이력</TableHeaderTitle>
          <PeriodText>{s.periodStartDate} ~ {s.periodEndDate} ({filteredPrivateLessons.length}건)</PeriodText>
        </TableHeaderLeft>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <TextField
            size="small"
            placeholder="회원 검색"
            value={memberKeyword}
            onChange={e => setMemberKeyword(e.target.value)}
            sx={{ width: '220px', '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: '18px', color: '#aaa' }} />
                </InputAdornment>
              ),
            }}
          />
          <CommonExcelButton onClick={handlePrivateExcel} disabled={!isCountType || filteredPrivateLessons.length === 0}>
            엑셀 다운로드
          </CommonExcelButton>
          <TotalAmountBox>
            <TotalAmountLabel>합계 :</TotalAmountLabel>
            <TotalAmountValue>{formatterCurrency(privateTotalAmount)}원</TotalAmountValue>
          </TotalAmountBox>
        </Box>
      </TableHeader>
      <StyledTable>
        <StyledTableHead>
          <StyledTableHeadCell flex="0.8">수업일</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.8">수업 시간</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.6">회원명</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.5">출결</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.6">사인 여부</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.6">신규 여부</StyledTableHeadCell>
          <StyledTableHeadCell flex="1.2">수강명</StyledTableHeadCell>
          <StyledTableHeadCell flex="1">판매&결제금액</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.8">회당 단가</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.6">적용 구분</StyledTableHeadCell>
          <StyledTableHeadCell flex="1">정산된 수업 수당</StyledTableHeadCell>
        </StyledTableHead>
        <StyledTableBody>
          {!isCountType && (
            <Box sx={{ p: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
              이 직원은 수업별 수당이 아닌 기본급으로 지급됩니다
            </Box>
          )}
          {isCountType && filteredPrivateLessons.map((l, idx) => (
            <StyledTableRow key={l.id} isEven={idx % 2 === 1}>
              <StyledTableCell flex="0.8">{l.lessonDate}</StyledTableCell>
              <StyledTableCell flex="0.8">{formatTime(l.startTime)}~{formatTime(l.endTime)}</StyledTableCell>
              <StyledTableCell flex="0.6">{l.memberName}</StyledTableCell>
              <StyledTableCell flex="0.5">{l.attendanceStatus === 'ATTENDED' ? '출석' : '결석'}</StyledTableCell>
              <StyledTableCell flex="0.6">
                <Chip
                  label={l.signed ? '완료' : '미완료'}
                  size="small"
                  sx={{
                    height: '20px', fontSize: '11px', fontWeight: 600,
                    backgroundColor: l.signed ? '#E9F9EE' : '#F5F5F5',
                    color: l.signed ? '#2E9E5B' : '#8c8c8c',
                  }}
                />
              </StyledTableCell>
              <StyledTableCell flex="0.6">{l.isNewMember === 'Y' ? '신규' : '재등록'}</StyledTableCell>
              <StyledTableCell flex="1.2">{l.ticketName}</StyledTableCell>
              <StyledTableCell flex="1" sx={{ flexDirection: 'column', gap: '2px', py: '6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <Box component="span" sx={{ fontSize: '11px', color: '#999', minWidth: '28px' }}>판매</Box>
                  <Box component="span">{formatterCurrency(l.saleAmount)}원</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <Box component="span" sx={{ fontSize: '11px', color: '#999', minWidth: '28px' }}>결제</Box>
                  <Box component="span">{formatterCurrency(l.paidAmount)}원</Box>
                </Box>
              </StyledTableCell>
              <StyledTableCell flex="0.8">{formatterCurrency(l.unitPrice)}원</StyledTableCell>
              <StyledTableCell flex="0.6">{l.rateLabel}</StyledTableCell>
              <StyledTableCell flex="1" highlight sx={{ flexDirection: 'column', gap: '2px', py: '6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box component="span" sx={{ minWidth: '64px', textAlign: 'right' }}>
                    {formatterCurrency(l.lessonAllowance)}원
                  </Box>
                  <Box sx={{ width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {!isConfirmed && (
                      <IconButton
                        size="small"
                        sx={{ p: '2px' }}
                        onClick={() => handleOpenEditLesson(l, 'PRIVATE', `${l.lessonDate} ${l.memberName} 개인레슨`)}
                      >
                        <EditOutlinedIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {l.edited && (
                  <Chip label="수정됨" size="small" sx={{ alignSelf: 'center', height: '16px', fontSize: '10px', backgroundColor: '#FFF1E6', color: '#FA8C16' }} />
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
          {isCountType && filteredPrivateLessons.length === 0 && (
            <Box sx={{ p: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>수업 이력이 없습니다</Box>
          )}
        </StyledTableBody>
      </StyledTable>
    </>
  );

  const renderGroupTable = () => (
    <>
      <TableHeader>
        <TableHeaderLeft>
          <TableHeaderTitle>그룹레슨 수업 이력</TableHeaderTitle>
          <PeriodText>{s.periodStartDate} ~ {s.periodEndDate} ({filteredGroupLessons.length}건)</PeriodText>
        </TableHeaderLeft>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CommonSelect
            value={rangeFilter}
            onChange={e => setRangeFilter(e.target.value)}
            options={rangeOptions}
            emptyLabel="정산 구간 : 전체"
            width={180}
          />
          <CommonExcelButton onClick={handleGroupExcel} disabled={!isCountType || filteredGroupLessons.length === 0}>
            엑셀 다운로드
          </CommonExcelButton>
          <TotalAmountBox>
            <TotalAmountLabel>합계 :</TotalAmountLabel>
            <TotalAmountValue>{formatterCurrency(groupTotalAmount)}원</TotalAmountValue>
          </TotalAmountBox>
        </Box>
      </TableHeader>
      <StyledTable>
        <StyledTableHead>
          <StyledTableHeadCell flex="0.8">수업일</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.8">수업 시간</StyledTableHeadCell>
          <StyledTableHeadCell flex="1">수업명</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.8">강의실</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.5">정원</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.5">수업 인원</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.5">출석 인원</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.5">결석 인원</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.6">사인 여부</StyledTableHeadCell>
          <StyledTableHeadCell flex="0.7">정산 구간</StyledTableHeadCell>
          <StyledTableHeadCell flex="1">정산된 수업 수당</StyledTableHeadCell>
        </StyledTableHead>
        <StyledTableBody>
          {!isCountType && (
            <Box sx={{ p: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
              이 직원은 수업별 수당이 아닌 기본급으로 지급됩니다
            </Box>
          )}
          {isCountType && filteredGroupLessons.map((l, idx) => (
            <StyledTableRow key={l.id} isEven={idx % 2 === 1}>
              <StyledTableCell flex="0.8">{l.lessonDate}</StyledTableCell>
              <StyledTableCell flex="0.8">{formatTime(l.startTime)}~{formatTime(l.endTime)}</StyledTableCell>
              <StyledTableCell flex="1">{l.className}</StyledTableCell>
              <StyledTableCell flex="0.8">{l.roomName}</StyledTableCell>
              <StyledTableCell flex="0.5">{l.capacity}명</StyledTableCell>
              <StyledTableCell flex="0.5">{l.enrolledCount}명</StyledTableCell>
              <StyledTableCell flex="0.5">{l.attendedCount}명</StyledTableCell>
              <StyledTableCell flex="0.5">{l.absentCount}명</StyledTableCell>
              <StyledTableCell flex="0.6">
                <Chip
                  label={l.signed ? '완료' : '미완료'}
                  size="small"
                  sx={{
                    height: '20px', fontSize: '11px', fontWeight: 600,
                    backgroundColor: l.signed ? '#E9F9EE' : '#F5F5F5',
                    color: l.signed ? '#2E9E5B' : '#8c8c8c',
                  }}
                />
              </StyledTableCell>
              <StyledTableCell flex="0.7">{l.rangeLabel}</StyledTableCell>
              <StyledTableCell flex="1" highlight sx={{ flexDirection: 'column', gap: '2px', py: '6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box component="span" sx={{ minWidth: '64px', textAlign: 'right' }}>
                    {formatterCurrency(l.lessonAllowance)}원
                  </Box>
                  <Box sx={{ width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {!isConfirmed && (
                      <IconButton
                        size="small"
                        sx={{ p: '2px' }}
                        onClick={() => handleOpenEditLesson(l, 'GROUP', `${l.lessonDate} ${l.className} 그룹레슨`)}
                      >
                        <EditOutlinedIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {l.edited && (
                  <Chip label="수정됨" size="small" sx={{ alignSelf: 'center', height: '16px', fontSize: '10px', backgroundColor: '#FFF1E6', color: '#FA8C16' }} />
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
          {isCountType && filteredGroupLessons.length === 0 && (
            <Box sx={{ p: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>수업 이력이 없습니다</Box>
          )}
        </StyledTableBody>
      </StyledTable>
    </>
  );

  // ============================================
  // Step 2: 급여 항목
  // ============================================
  const renderPayItem = (title, amount, targetKey, subItems) => (
    <PayItemCard>
      <PayItemRow>
        <PayItemTitle>{title}</PayItemTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PayItemAmount>{amount >= 0 ? '+' : ''}{formatterCurrency(amount)}원</PayItemAmount>
          {!isConfirmed && (
            <AdjustButton onClick={() => handleOpenAdjust(title, targetKey === 'DEDUCTION' ? 'deduct' : 'pay', amount, targetKey)}>
              금액조정
            </AdjustButton>
          )}
        </Box>
      </PayItemRow>
      {subItems?.map((sub, i) => (
        <PayItemSubRow key={i}>
          <PayItemSubLabel>{sub.label}</PayItemSubLabel>
          <PayItemSubAmount>{sub.amount}</PayItemSubAmount>
        </PayItemSubRow>
      ))}
    </PayItemCard>
  );

  const renderStep2 = () => (
    <>
      <SummaryCardsContainer>
        <PaySummaryCard variant="pay">
          <PaySummaryIcon variant="pay">+</PaySummaryIcon>
          <Box><PaySummaryLabel>지급 합계</PaySummaryLabel><PaySummaryValue>{formatterCurrency(s.totalPayAmount || 0)}원</PaySummaryValue></Box>
        </PaySummaryCard>
        <PaySummaryCard variant="deduct">
          <PaySummaryIcon variant="deduct">−</PaySummaryIcon>
          <Box><PaySummaryLabel>공제 합계</PaySummaryLabel><PaySummaryValue>{formatterCurrency(s.deductionAmount || 0)}원</PaySummaryValue></Box>
        </PaySummaryCard>
        <PaySummaryCard variant="total">
          <PaySummaryIcon variant="total">W</PaySummaryIcon>
          <Box><PaySummaryLabel variant="total">최종 실지급액</PaySummaryLabel><PaySummaryValue variant="total">{formatterCurrency(s.netPayAmount || 0)}원</PaySummaryValue></Box>
        </PaySummaryCard>
      </SummaryCardsContainer>

      <PayItemsContainer>
        <PayItemsColumn flex={2}>
          <PayItemsWrapper>
            <PayItemsHeader>
              <PayItemsHeaderTitle><span style={{ color: '#4AB3BC' }}>●</span> 지급 항목</PayItemsHeaderTitle>
              <PayItemsHeaderAmount>{formatterCurrency(s.totalPayAmount || 0)}원</PayItemsHeaderAmount>
            </PayItemsHeader>
            {renderPayItem('기본급', s.baseAmount || 0, 'BASE')}
            {renderPayItem('개인레슨 수당', s.privateLessonAmount || 0, 'PRIVATE_LESSON')}
            {renderPayItem('그룹레슨 수당', s.groupLessonAmount || 0, 'GROUP_LESSON')}
            {renderPayItem('초과 매출/수업 수당', s.overflowAmount || 0, 'OVERFLOW')}
            {renderPayItem('성과급', s.bonusAmount || 0, 'BONUS')}
            {renderPayItem('비과세 항목', s.taxExemptAmount || 0, 'TAX_EXEMPT')}
            {/* 근로수당도 지급 항목이다. 확정 여부와 무관하게 목록에 넣어야
                항목 합계와 지급 합계가 어긋나지 않는다. */}
            {s.overtimeAmount != null && renderPayItem('연장근로 수당', s.overtimeAmount || 0, 'OVERTIME')}
            {s.nightWorkAmount != null && renderPayItem('야간근로 수당', s.nightWorkAmount || 0, 'NIGHT_WORK')}
            {s.holidayWorkAmount != null && renderPayItem('휴일근로 수당', s.holidayWorkAmount || 0, 'HOLIDAY_WORK')}
            {s.weeklyHolidayAmount != null && renderPayItem('주휴 수당', s.weeklyHolidayAmount || 0, 'WEEKLY_HOLIDAY')}

            {/* 미확정 상태에서는 근로시간을 수기로 고칠 수 있게 입력란을 덧붙인다 */}
            {(s.salaryType === 'MONTHLY' || s.salaryType === 'HOURLY') && !isConfirmed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '8px', p: '12px', background: '#F7F9FA', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555', mb: '4px' }}>근로시간 입력</Typography>
                {s.salaryType === 'HOURLY' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pb: '8px', borderBottom: '1px solid #E8E8E8', mb: '4px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#333', width: '70px', fontWeight: 600 }}>총 근무</Typography>
                    <input
                      type="number" min="0" step="0.5"
                      value={workHours.totalWorkHours || ''}
                      onChange={e => setWorkHours(prev => ({ ...prev, totalWorkHours: Number(e.target.value) || 0 }))}
                      placeholder="0"
                      style={{ width: '60px', height: '30px', padding: '0 8px', border: '1px solid #4AB3BC', borderRadius: '6px', fontSize: '13px', textAlign: 'center' }}
                    />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>시간</Typography>
                    {workHours.totalWorkHours > 0 && settledHourlyRate > 0 && (
                      <Typography sx={{ fontSize: '12px', color: isWorkHoursInvalid ? '#dc2626' : '#4AB3BC', fontWeight: 600 }}>
                        {isWorkHoursInvalid
                          ? '총 근무시간이 연장·휴일 합보다 적습니다'
                          : `기본급 = ${formatterCurrency(settledHourlyRate)}원 × ${scheduledHours}h(연장·휴일 제외) = ${formatterCurrency(Math.floor(settledHourlyRate * scheduledHours))}원`}
                      </Typography>
                    )}
                  </Box>
                )}
                {[
                  { key: 'overtimeHours', label: '연장근로', enabled: true },
                  { key: 'nightWorkHours', label: '야간근로', enabled: true },
                  { key: 'holidayWorkHours', label: '휴일근로', enabled: true },
                  ...(s.salaryType === 'HOURLY' ? [{ key: 'weeklyHolidayHours', label: '주휴', enabled: true }] : []),
                ].map(item => (
                  <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#666', width: '70px' }}>{item.label}</Typography>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={workHours[item.key] || ''}
                      onChange={e => setWorkHours(prev => ({ ...prev, [item.key]: Number(e.target.value) || 0 }))}
                      placeholder="0"
                      style={{ width: '60px', height: '30px', padding: '0 8px', border: '1px solid #D9D9D9', borderRadius: '6px', fontSize: '13px', textAlign: 'center' }}
                    />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>시간</Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '4px' }}>
                  <Box onClick={() => {
                      if (isWorkHoursInvalid) {
                        toast.error('총 근무시간은 연장·휴일 근로시간의 합 이상이어야 합니다');
                        return;
                      }
                      workHoursMutation.mutate();
                    }}
                    sx={{ padding: '4px 14px', borderRadius: '6px', background: isWorkHoursInvalid ? '#C4C4C4' : '#4AB3BC', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: isWorkHoursInvalid ? 'not-allowed' : 'pointer', '&:hover': { background: isWorkHoursInvalid ? '#C4C4C4' : '#3a9ca4' } }}>
                    {workHoursMutation.isPending ? '적용 중...' : '수당 적용'}
                  </Box>
                </Box>
                {s.overtimeAmount > 0 && <Typography sx={{ fontSize: '12px', color: '#4AB3BC' }}>연장근로 {formatterCurrency(s.overtimeAmount)}원</Typography>}
                {s.nightWorkAmount > 0 && <Typography sx={{ fontSize: '12px', color: '#4AB3BC' }}>야간근로 {formatterCurrency(s.nightWorkAmount)}원</Typography>}
                {s.holidayWorkAmount > 0 && <Typography sx={{ fontSize: '12px', color: '#4AB3BC' }}>휴일근로 {formatterCurrency(s.holidayWorkAmount)}원</Typography>}
                {s.weeklyHolidayAmount > 0 && <Typography sx={{ fontSize: '12px', color: '#4AB3BC' }}>주휴 {formatterCurrency(s.weeklyHolidayAmount)}원</Typography>}
              </Box>
            )}
          </PayItemsWrapper>
        </PayItemsColumn>

        <PayItemsColumn flex={1}>
          <PayItemsWrapper>
            <PayItemsHeader>
              <PayItemsHeaderTitle><span style={{ color: '#CF1322' }}>●</span> 공제 항목</PayItemsHeaderTitle>
              <PayItemsHeaderAmount>{formatterCurrency(s.deductionAmount || 0)}원</PayItemsHeaderAmount>
            </PayItemsHeader>
            {(s.deductionDetails || []).map((d, i) => (
              <PayItemCard key={i}>
                <PayItemRow>
                  <PayItemTitle>{d.itemName}</PayItemTitle>
                  <PayItemAmount color="#CF1322">-{formatterCurrency(d.amount)}원</PayItemAmount>
                </PayItemRow>
              </PayItemCard>
            ))}
            {(!s.deductionDetails || s.deductionDetails.length === 0) && (
              <PayItemCard><PayItemSubLabel>공제 항목 없음</PayItemSubLabel></PayItemCard>
            )}
          </PayItemsWrapper>
        </PayItemsColumn>
      </PayItemsContainer>
    </>
  );

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `${s.settlementYear}년${s.settlementMonth}월_${s.branchName || ''}_${s.employeeName}_급여명세서`;
    window.print();
    document.title = originalTitle;
  };

  // ============================================
  // Step 3: 명세서
  // ============================================
  const renderPayslipItem = (title, amount, subItems = []) => (
    <PayslipItemBox>
      <PayslipItemBoxHeader>
        <PayslipItemBoxTitle>{title}</PayslipItemBoxTitle>
        <PayslipItemBoxAmount>
          {amount >= 0 ? '+' : ''}{formatterCurrency(amount || 0)}원
        </PayslipItemBoxAmount>
      </PayslipItemBoxHeader>
      {subItems.map((sub, i) => (
        <PayslipItemBoxSubRow key={i}>
          <PayslipItemBoxSubLabel>{sub.label}</PayslipItemBoxSubLabel>
          <PayslipItemBoxSubAmount>{sub.amount}</PayslipItemBoxSubAmount>
        </PayslipItemBoxSubRow>
      ))}
    </PayslipItemBox>
  );

  // 근무시간 출처 배지. 출퇴근 기록이 없어 계약시간으로 대체된 경우를 명세서에서 구분할 수 있어야 한다.
  const WORK_HOURS_SOURCE_LABEL = {
    ATTENDANCE: '출퇴근 기록',
    CONTRACT: '계약 소정근로시간',
    MANUAL: '관리자 입력',
  };

  const formatHours = minutes => {
    if (minutes == null) return null;
    const hours = minutes / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}시간`;
  };

  // 기본급이 덮는 소정근로시간. 연장·휴일 시간은 각 수당이 근로분까지 지급하므로 기본급에서 빠져 있다.
  const scheduledMinutes =
    s.totalWorkMinutes != null
      ? Math.max(0, s.totalWorkMinutes - (s.overtimeMinutes || 0) - (s.holidayWorkMinutes || 0))
      : null;

  // 시간제만 시급을 역산한다. 기본급 = 시급 × 소정근로시간 이므로 정확히 되돌아온다.
  const derivedHourlyRate =
    s.salaryType === 'HOURLY' && scheduledMinutes > 0 && s.baseAmount
      ? Math.round((s.baseAmount * 60) / scheduledMinutes)
      : null;

  // 계산 근거 행. 근로기준법 시행령 제27조의2 가 연장·야간·휴일근로의 "시간 수" 기재를 요구한다.
  // 요율은 금액에서 역산하지 않는다 — 휴일근로는 8시간 이내와 초과분의 가산율이 달라
  // 단일 요율로 되돌리면 실제로 적용된 적 없는 값이 나온다.
  const buildBasisSubItems = kind => {
    const rows = [];
    const push = (label, amount) => amount && rows.push({ label, amount });

    switch (kind) {
      case 'BASE':
        if (scheduledMinutes) {
          push(
            derivedHourlyRate
              ? `소정근로 ${formatHours(scheduledMinutes)} × ${formatterCurrency(derivedHourlyRate)}원`
              : `소정근로 ${formatHours(scheduledMinutes)}`,
            WORK_HOURS_SOURCE_LABEL[s.workHoursSource] || '근무시간 기준'
          );
        }
        break;
      case 'OVERTIME':
        push('연장근로 시간', formatHours(s.overtimeMinutes));
        break;
      case 'NIGHT_WORK':
        push('야간근로 시간', formatHours(s.nightWorkMinutes));
        break;
      case 'HOLIDAY_WORK':
        push('휴일근로 시간', formatHours(s.holidayWorkMinutes));
        // 1일 8시간 초과분은 가산율이 다르므로 따로 밝힌다.
        if (s.holidayWorkMinutes > 480) {
          push('8시간 초과분', formatHours(s.holidayWorkMinutes - 480));
        }
        break;
      case 'WEEKLY_HOLIDAY':
        push('주휴 산정 시간', s.weeklyHolidayHours != null ? `${Number(s.weeklyHolidayHours)}시간` : null);
        break;
      case 'PRIVATE_LESSON':
        push('정산 수업', s.privateLessonCount ? `${s.privateLessonCount}건` : null);
        break;
      case 'GROUP_LESSON':
        push('정산 수업', s.groupLessonCount ? `${s.groupLessonCount}건` : null);
        break;
      default:
        break;
    }
    return rows;
  };

  // 공제는 "공제 방식(부모) - 세부 항목(자식)" 구조로 보여준다.
  // 3.3%·4대보험은 세부 항목이 여러 개라 묶어야 읽힌다. 백엔드가 묶어 내려주고,
  // 아직 그 필드가 없는 응답(구버전)에서는 기존처럼 평면 목록으로 떨어진다.
  const deductionRows = (s.deductionGroups && s.deductionGroups.length)
    ? s.deductionGroups
    : (s.deductionDetails || []).map(d => ({ itemName: d.itemName, amount: d.amount, subItems: [] }));

  // 백엔드가 내려주는 금액 분해(기본 수당 / 개별 수당 …).
  // 분해 합이 저장된 금액과 어긋나면 백엔드가 null 을 주므로, 그때는 행을 만들지 않는다.
  const BREAKDOWN_FIELD = {
    PRIVATE_LESSON: 'privateLessonBreakdown',
    GROUP_LESSON: 'groupLessonBreakdown',
    OVERFLOW: 'overflowBreakdown',
    BONUS: 'bonusBreakdown',
    TAX_EXEMPT: 'taxExemptBreakdown',
  };

  const buildAmountSubItems = kind => {
    const rows = s[BREAKDOWN_FIELD[kind]];
    if (!rows || !rows.length) return [];
    return rows.map(r => ({ label: r.label, amount: `+${formatterCurrency(r.amount)}원` }));
  };

  // 금액 분해를 먼저, 계산 근거(시간·건수)를 다음, 사후 조정을 뒤에 둔다.
  const subItemsFor = kind => [
    ...buildAmountSubItems(kind),
    ...buildBasisSubItems(kind),
    ...buildAdjustmentSubItems(kind),
  ];

  const buildAdjustmentSubItems = targetItem => {
    return (s.adjustments || [])
      .filter(adj => adj.targetItem === targetItem)
      .map(adj => ({
        label: `조정액 · ${adj.memo || ''}`,
        amount: `${adj.adjustType === 'ADD' ? '+' : '-'}${formatterCurrency(adj.amount)}원`,
      }));
  };

  const renderStep3 = () => (
    <PayslipContainer>
      <style>{`
        @media print {
          @page { margin: 0; size: A4; }
          body > * { visibility: hidden; }
          #payslip-print-area, #payslip-print-area * {
            visibility: visible;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          #payslip-print-area {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            padding: 15mm;
            box-sizing: border-box;
            background: #fff !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <div id="payslip-print-area" style={{ display: 'flex', gap: '20px', flex: 1 }}>
        <PayslipCard>
          <PayslipHeader>
            <PayslipHeaderLeft>
              <PayslipBranchName>{s.branchName || ''}</PayslipBranchName>
              <PayslipTitle>{s.settlementYear}년 {s.settlementMonth}월 급여명세서</PayslipTitle>
              <PayslipEmployeeInfo>
                <PayslipEmployeeInfoText>
                  {s.employeeName}
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>({CLASSIFICATION_LABELS[s.employeeClassification] || '재직중'})</span>
                </PayslipEmployeeInfoText>
                {s.employeePosition && (
                  <>
                    <PayslipEmployeeInfoDivider />
                    <PayslipEmployeeInfoText sx={{ fontSize: '13px' }}>{s.employeePosition}</PayslipEmployeeInfoText>
                  </>
                )}
                <PayslipEmployeeInfoDivider />
                <PayslipEmployeeInfoText sx={{ fontSize: '13px' }}>{SALARY_TYPE_LABELS[s.salaryType] || ''}</PayslipEmployeeInfoText>
                {s.employeePhone && (
                  <>
                    <PayslipEmployeeInfoDivider />
                    <PayslipEmployeeInfoText sx={{ fontSize: '13px' }}>{s.employeePhone}</PayslipEmployeeInfoText>
                  </>
                )}
              </PayslipEmployeeInfo>
            </PayslipHeaderLeft>
            <PayslipHeaderRight>
              <PayslipRecipientLabel>지급 대상</PayslipRecipientLabel>
              <PayslipRecipientName>{s.employeeName}</PayslipRecipientName>
              {s.salaryType && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#55C982', borderRadius: '4px', padding: '2px 10px', fontSize: '13px', fontWeight: 700, color: '#fff' }}>
                  {SALARY_TYPE_LABELS[s.salaryType]}
                </Box>
              )}
              <Box
                className="no-print"
                onClick={handlePrint}
                sx={{
                  padding: '4px 12px', borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.5)', color: 'rgba(255,255,255,0.8)',
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginTop: '4px',
                  '&:hover': { background: 'rgba(255,255,255,0.1)' },
                }}
              >
                🖨 인쇄 / PDF
              </Box>
            </PayslipHeaderRight>
          </PayslipHeader>

          <PayslipBody>
            {/* 지급 합계 섹션 */}
            <PayslipSection>
              <PayslipSectionHeader>
                <PayslipSectionTitleBox>
                  <PayslipSectionTitle>
                    <PayslipSectionDot dotColor="#4AB3BC" />
                    지급 합계
                  </PayslipSectionTitle>
                  <PayslipSectionSubtitle>
                    <Box sx={{ width: '12px' }} />
                    기본급 · 수업수당 · 초과매출/수업수당 · 성과급 · 비과세 · 근로수당
                  </PayslipSectionSubtitle>
                </PayslipSectionTitleBox>
                <PayslipSectionAmount color="#4AB3BC">
                  {formatterCurrency(s.totalPayAmount || 0)}원
                </PayslipSectionAmount>
              </PayslipSectionHeader>
              <PayslipDivider />
              <PayslipItemsGrid>
                {renderPayslipItem('기본급', s.baseAmount, subItemsFor('BASE'))}
                {renderPayslipItem('개인레슨 수당', s.privateLessonAmount, subItemsFor('PRIVATE_LESSON'))}
              </PayslipItemsGrid>
              <PayslipItemsGrid>
                {renderPayslipItem('그룹레슨 수당', s.groupLessonAmount, subItemsFor('GROUP_LESSON'))}
                {renderPayslipItem('초과 매출/수업 수당', s.overflowAmount, subItemsFor('OVERFLOW'))}
              </PayslipItemsGrid>
              <PayslipItemsGrid>
                {renderPayslipItem('성과급', s.bonusAmount, subItemsFor('BONUS'))}
                {renderPayslipItem('비과세 항목', s.taxExemptAmount, subItemsFor('TAX_EXEMPT'))}
              </PayslipItemsGrid>
              {(s.overtimeAmount != null || s.nightWorkAmount != null || s.holidayWorkAmount != null || s.weeklyHolidayAmount != null) && (
                <PayslipItemsGrid>
                  {s.overtimeAmount != null && renderPayslipItem('연장근로 수당', s.overtimeAmount || 0, subItemsFor('OVERTIME'))}
                  {s.nightWorkAmount != null && renderPayslipItem('야간근로 수당', s.nightWorkAmount || 0, subItemsFor('NIGHT_WORK'))}
                  {s.holidayWorkAmount != null && renderPayslipItem('휴일근로 수당', s.holidayWorkAmount || 0, subItemsFor('HOLIDAY_WORK'))}
                  {s.weeklyHolidayAmount != null && renderPayslipItem('주휴 수당', s.weeklyHolidayAmount || 0, subItemsFor('WEEKLY_HOLIDAY'))}
                </PayslipItemsGrid>
              )}
            </PayslipSection>

            {/* 공제 합계 섹션 */}
            <PayslipSection>
              <PayslipSectionHeader>
                <PayslipSectionTitleBox>
                  <PayslipSectionTitle>
                    <PayslipSectionDot dotColor="#EE735A" />
                    공제 합계
                  </PayslipSectionTitle>
                  <PayslipSectionSubtitle>
                    <Box sx={{ width: '12px' }} />
                    {(deductionRows.length
                      ? deductionRows.map(d => d.itemName)
                      : []).join(' · ') || '공제 항목'}
                  </PayslipSectionSubtitle>
                </PayslipSectionTitleBox>
                <PayslipSectionAmount color="#EE735A">
                  {formatterCurrency(s.deductionAmount || 0)}원
                </PayslipSectionAmount>
              </PayslipSectionHeader>
              <PayslipDivider />
              <PayslipItemsGrid style={{ flexDirection: 'column' }}>
                {deductionRows.map((d, i) => (
                  <PayslipItemBox key={i}>
                    <PayslipItemBoxHeader>
                      <PayslipItemBoxTitle>{d.itemName}</PayslipItemBoxTitle>
                      <PayslipItemBoxAmount sx={{ color: '#333' }}>
                        -{formatterCurrency(d.amount)}원
                      </PayslipItemBoxAmount>
                    </PayslipItemBoxHeader>
                    {(d.subItems || []).map((sub, j) => (
                      <PayslipItemBoxSubRow key={j}>
                        <PayslipItemBoxSubLabel>{sub.label}</PayslipItemBoxSubLabel>
                        <PayslipItemBoxSubAmount>-{formatterCurrency(sub.amount)}원</PayslipItemBoxSubAmount>
                      </PayslipItemBoxSubRow>
                    ))}
                  </PayslipItemBox>
                ))}
                {deductionRows.length === 0 && (
                  <Box sx={{ padding: '12px 16px', color: '#a6a6a6', fontSize: '13px' }}>공제 항목 없음</Box>
                )}
              </PayslipItemsGrid>
            </PayslipSection>

            {/* 실지급액 */}
            <PayslipTotalBox>
              <PayslipTotalLabel>
                <PayslipTotalTitle>
                  <PayslipSectionDot dotColor="#4AB3BC" />
                  실지급액
                </PayslipTotalTitle>
                <PayslipTotalSub>지급 합계 - 공제 합계</PayslipTotalSub>
              </PayslipTotalLabel>
              <PayslipTotalAmount>{formatterCurrency(s.netPayAmount || 0)}원</PayslipTotalAmount>
            </PayslipTotalBox>

            <PayslipFooterText>귀하의 노고에 감사드립니다.</PayslipFooterText>
          </PayslipBody>
        </PayslipCard>

        <RecentPayCard className="no-print">
          <RecentPayHeader>
            <RecentPayTitle>최근급여 이력</RecentPayTitle>
            <RecentPayPeriod>최근 6개월</RecentPayPeriod>
          </RecentPayHeader>
          <RecentPayTableWrapper>
            <RecentPayTableHead>
              <RecentPayTableHeadCell sx={{ width: '80px', borderRight: '1px solid #f2f2f2' }}>월</RecentPayTableHeadCell>
              <RecentPayTableHeadCell sx={{ flex: 1, justifyContent: 'flex-end' }}>실지급액</RecentPayTableHeadCell>
            </RecentPayTableHead>
            {recentPay.map((item, i) => (
              <RecentPayRow key={i}>
                <RecentPayMonth>{item.month}월</RecentPayMonth>
                <RecentPayAmount>{formatterCurrency(item.netPayAmount)}원</RecentPayAmount>
              </RecentPayRow>
            ))}
            {recentPay.length === 0 && (
              <Box sx={{ p: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>이력 없음</Box>
            )}
          </RecentPayTableWrapper>
        </RecentPayCard>
      </div>
    </PayslipContainer>
  );

  // ============================================
  // 좌측 프로필
  // ============================================
  const renderBottomCard = () => {
    if (lessonTab === 'group' && currentStep === 1) {
      const rangeStats = {};
      (groupLessons || []).forEach(l => {
        const label = l.rangeLabel || '0명';
        if (!rangeStats[label]) rangeStats[label] = { count: 0, total: 0 };
        rangeStats[label].count += 1;
        rangeStats[label].total += l.lessonAllowance || 0;
      });
      const entries = Object.entries(rangeStats);
      return (
        <RegistrationCard>
          <RegistrationTitle>
            구간 수당별 현황
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#8c8c8c' }}>
              총 {groupLessons?.length || 0}건
            </span>
          </RegistrationTitle>
          {entries.map(([label, stat]) => (
            <RegistrationRow key={label}>
              <RegistrationLabel>{label}</RegistrationLabel>
              <RegistrationValue color="#4AB3BC">
                {stat.count}건 · {formatterCurrency(stat.total)}원
              </RegistrationValue>
            </RegistrationRow>
          ))}
          {entries.length === 0 && (
            <RegistrationRow>
              <RegistrationLabel style={{ color: '#bbb' }}>그룹레슨 이력이 없습니다</RegistrationLabel>
            </RegistrationRow>
          )}
        </RegistrationCard>
      );
    }
    return (
      <RegistrationCard>
        <RegistrationTitle>
          신규 · 재등록 현황
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#8c8c8c' }}>
            총 {(s.newMemberCount || 0) + (s.renewMemberCount || 0)}건
          </span>
        </RegistrationTitle>
        <RegistrationRow>
          <RegistrationLabel>신규</RegistrationLabel>
          <RegistrationValue color="#4AB3BC">{s.newMemberCount || 0}건</RegistrationValue>
        </RegistrationRow>
        <RegistrationRow>
          <RegistrationLabel>재등록</RegistrationLabel>
          <RegistrationValue color="#FA8C16">{s.renewMemberCount || 0}건</RegistrationValue>
        </RegistrationRow>
      </RegistrationCard>
    );
  };

  if (!settlement) {
    return <AnimationPage><Box sx={{ p: '60px', textAlign: 'center', color: '#999' }}>정산 데이터를 불러오는 중...</Box></AnimationPage>;
  }

  const renderEarlySettlementNotice = () => {
    if (!earlySettlementNotice) return null;
    const n = earlySettlementNotice;
    return (
      <EarlyNoticeBox>
        <EarlyNoticeTitle>산정 기간이 아직 끝나지 않았습니다</EarlyNoticeTitle>
        <EarlyNoticeRow>
          <span>산정 기간</span>
          <b>{n.periodStart} ~ {n.periodEnd}</b>
        </EarlyNoticeRow>
        <EarlyNoticeRow>
          <span>오늘</span>
          <b>{n.today} ({n.daysLeft}일 남음)</b>
        </EarlyNoticeRow>
        <EarlyNoticeRow>
          <span>수업 반영</span>
          <b>{n.totalCount}건 중 {n.settledCount}건</b>
        </EarlyNoticeRow>
        {n.pendingCount > 0 && (
          <EarlyNoticeDesc>
            나머지 {n.pendingCount}건은 아직 진행 전이라 출결이 없어 <b>인원 0명</b>으로 계산됩니다.
          </EarlyNoticeDesc>
        )}
      </EarlyNoticeBox>
    );
  };

  const isProcessing = adjustMutation.isPending || confirmMutation.isPending || deleteMutation.isPending || recalculateMutation.isPending;
  const processingLabel = deleteMutation.isPending ? '삭제 중...' : confirmMutation.isPending ? '정산 확정 중...' : recalculateMutation.isPending ? '재계산 중...' : '처리 중...';

  return (
    <AnimationPage>
      <Backdrop open={isProcessing} sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: '16px' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{processingLabel}</Typography>
      </Backdrop>
      <PageContainer>
        <ProfilePanel>
          <ProfileCard>
            <ProfileAvatar src={s.employeeProfileImage || undefined} sx={{ width: 120, height: 120 }} />
            <Box sx={{ textAlign: 'center' }}>
              <ProfileName>
                {s.employeeName || `직원 #${s.employeeId}`}
                {s.salaryType && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#55C982', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', fontWeight: 700, color: '#fff' }}>
                    {SALARY_TYPE_LABELS[s.salaryType]}
                  </Box>
                )}
              </ProfileName>
              <ProfilePosition>{s.employeePosition || ''}</ProfilePosition>
            </Box>
            <ProfileInfoTable>
              <ProfileInfoRow><ProfileInfoLabel>지점</ProfileInfoLabel><ProfileInfoValue>{s.branchName || ''}</ProfileInfoValue></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>재직 상태</ProfileInfoLabel><ProfileInfoValue>{CLASSIFICATION_LABELS[s.employeeClassification] || '재직중'}</ProfileInfoValue></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>입사일</ProfileInfoLabel><ProfileInfoValue>{s.hireDate || ''}</ProfileInfoValue></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>연락처</ProfileInfoLabel><ProfileInfoValue>{s.employeePhone || '-'}</ProfileInfoValue></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>정산 수업</ProfileInfoLabel><ProfileInfoHighlight color="#4AB3BC">{s.totalLessonCount || 0}회</ProfileInfoHighlight></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>수업일수</ProfileInfoLabel><ProfileInfoValue>{s.workDays || 0}일</ProfileInfoValue></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>정산상태</ProfileInfoLabel><ProfileInfoHighlight color={s.status === 'CONFIRMED' ? '#595959' : '#EE735A'}>{STATUS_LABELS[s.status]}</ProfileInfoHighlight></ProfileInfoRow>
              <ProfileInfoRow><ProfileInfoLabel>정산일시</ProfileInfoLabel><ProfileInfoValue>{s.confirmedAt ? s.confirmedAt.replace('T', ' ').substring(0, 16) : '-'}</ProfileInfoValue></ProfileInfoRow>
            </ProfileInfoTable>
          </ProfileCard>
          {renderBottomCard()}
        </ProfilePanel>

        <ContentPanel>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <StepTabsContainer>
              {STEPS.map((step, idx) => (
                <Box key={step.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <StepTab active={currentStep === step.id} onClick={() => setCurrentStep(step.id)}>
                    <StepNumber active={currentStep === step.id}>{step.id}</StepNumber>
                    {step.label}
                  </StepTab>
                  {idx < STEPS.length - 1 && <StepConnector><ArrowForwardIosIcon sx={{ fontSize: '14px' }} /></StepConnector>}
                </Box>
              ))}
            </StepTabsContainer>
            <BackToListButton onClick={handleCancel}>← 목록으로</BackToListButton>
          </Box>

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          <FooterBar>
            {!isConfirmed && (
              <CancelButton onClick={() => setDeleteModalOpen(true)} style={{ backgroundColor: '#CF1322', color: '#fff', borderColor: '#CF1322' }}>
                삭제
              </CancelButton>
            )}
            {!isConfirmed && (
              <PrevButton onClick={handleOpenRecalculate} disabled={recalculateMutation.isPending}
                style={{ backgroundColor: '#404040', color: '#fff', borderColor: '#404040' }}>
                재계산
              </PrevButton>
            )}
            {currentStep > 1 && <PrevButton onClick={handlePrev}>이전</PrevButton>}
            {currentStep < 3 && (
              <NextButton onClick={handleNext}>다음</NextButton>
            )}
            {!isConfirmed && currentStep === 3 && (
              <NextButton onClick={() => setConfirmModalOpen(true)}>정산완료</NextButton>
            )}
          </FooterBar>
        </ContentPanel>
      </PageContainer>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} disableScrollLock>
        <ConfirmModalContainer>
          <ConfirmModalText>
            정산을 <span style={{ color: '#CF1322', fontWeight: 700, fontSize: '26px' }}>삭제</span>하시겠습니까?<br />삭제된 정산은 복구할 수 없습니다.
          </ConfirmModalText>
          <ConfirmModalButtons>
            <CommonModalButton variant="cancel" onClick={() => setDeleteModalOpen(false)}>닫기</CommonModalButton>
            <CommonModalButton variant="delete" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>삭제</CommonModalButton>
          </ConfirmModalButtons>
        </ConfirmModalContainer>
      </Modal>

      <Modal open={recalculateModalOpen} onClose={() => setRecalculateModalOpen(false)} disableScrollLock>
        <ConfirmModalContainer>
          <ConfirmModalText>
            정산을 <span style={{ color: '#4AB3BC', fontWeight: 700, fontSize: '26px' }}>재계산</span>하시겠습니까?<br />기존 정산 데이터가 새로 계산됩니다.
            {renderEarlySettlementNotice()}
            {/* 재계산은 정산을 삭제하고 새로 만들기 때문에 조정 내역이 함께 사라진다.
                되돌릴 수 없으므로 조정이 있을 때만 건수를 밝혀 경고한다. */}
            {(s.adjustments || []).length > 0 && (
              <Box sx={{ mt: '14px', fontSize: '15px', color: '#EE735A', fontWeight: 500, lineHeight: 1.6 }}>
                직접 입력한 <strong>금액 조정 {(s.adjustments || []).length}건이 삭제</strong>됩니다.<br />
                재계산 후에는 되돌릴 수 없습니다.
              </Box>
            )}
            {/* 재계산은 기존 정산(수업이력 포함)을 통째로 삭제하고 새로 만들기 때문에
                수업이력 탭에서 직접 수정한 개별 수당도 원래는 함께 사라진다.
                아래에서 체크한 항목만 재계산 후에도 값을 유지한다. */}
            {editedLessons.length > 0 && (
              <Box sx={{ mt: '14px', textAlign: 'left' }}>
                <Box sx={{ fontSize: '15px', color: '#EE735A', fontWeight: 500, lineHeight: 1.6, mb: '8px' }}>
                  수업이력에서 직접 수정한 <strong>개별 수업 수당 {editedLessons.length}건</strong>이 있습니다.<br />
                  재계산 후에도 유지할 항목을 선택해 주세요. 체크 해제한 항목은 초기화됩니다.
                </Box>
                <Box sx={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                  {editedLessons.map(l => (
                    <Box key={l.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px', p: '10px 12px', borderBottom: '1px solid #f5f5f5' }}>
                      <Checkbox
                        size="small"
                        checked={keepLessonIds.has(l.id)}
                        onChange={() => toggleKeepLesson(l.id)}
                        sx={{ p: 0, mt: '2px' }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#262626' }}>
                          {l.lessonDate} {l.memberName || l.className}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#8c8c8c' }}>
                          {formatterCurrency(l.previousAmount ?? 0)}원 → {formatterCurrency(l.lessonAllowance)}원
                        </Typography>
                        {l.editMemo && (
                          <Typography sx={{ fontSize: '12px', color: '#595959', mt: '2px' }}>
                            사유 · {l.editMemo}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ConfirmModalText>
          <ConfirmModalButtons>
            <CommonModalButton variant="cancel" onClick={() => setRecalculateModalOpen(false)}>닫기</CommonModalButton>
            <CommonModalButton
              variant="submit"
              onClick={() => { setRecalculateModalOpen(false); recalculateMutation.mutate(Array.from(keepLessonIds)); }}
              disabled={recalculateMutation.isPending}
            >
              재계산
            </CommonModalButton>
          </ConfirmModalButtons>
        </ConfirmModalContainer>
      </Modal>

      <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} disableScrollLock>
        <ConfirmModalContainer>
          <ConfirmModalText>
            정산을 <span style={{ color: '#4AB3BC', fontWeight: 700, fontSize: '26px' }}>완료</span>하시겠습니까?<br />정산 완료 후에는 수정이 불가능합니다.
            {renderEarlySettlementNotice()}
          </ConfirmModalText>
          <ConfirmModalButtons>
            <CommonModalButton variant="cancel" onClick={() => setConfirmModalOpen(false)}>닫기</CommonModalButton>
            <CommonModalButton variant="submit" onClick={() => confirmMutation.mutate()} disabled={confirmMutation.isPending}>확인</CommonModalButton>
          </ConfirmModalButtons>
        </ConfirmModalContainer>
      </Modal>

      <AdjustAmountModal
        open={adjustModalOpen}
        onClose={() => setAdjustModalOpen(false)}
        itemName={adjustTarget?.itemName || ''}
        itemType={adjustTarget?.itemType || 'pay'}
        currentAmount={adjustTarget?.currentAmount || 0}
        onSave={data => {
          adjustMutation.mutate({
            targetItem: adjustTarget?.targetKey,
            adjustType: data.type === 'add' ? 'ADD' : 'SUBTRACT',
            amount: data.amount,
            memo: data.memo,
          });
        }}
      />

      <EditLessonAllowanceModal
        open={editLessonModalOpen}
        onClose={() => setEditLessonModalOpen(false)}
        lessonLabel={editLessonTarget?.lessonLabel || ''}
        currentAmount={editLessonTarget?.currentAmount || 0}
        initialMemo={editLessonTarget?.editMemo}
        onSave={data => editLessonMutation.mutate(data)}
      />
    </AnimationPage>
  );
};

export default SalarySettlementDetail;
